import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import {
  NotificationsDataService,
  PaginatedNotificationsResult,
} from '@app/account/notifications/services/notifications-data.service';
import {
  ChannelNotification,
  ChannelNotificationFilterAttributes,
  NotificationRuleSeverityEnum,
} from '@app/generated/graphql';
import {
  catchError,
  combineLatest,
  EMPTY,
  map,
  Observable,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { captureException } from '@sentry/angular';
import { MessageService } from 'primeng/api';
import { NotificationAlertFilterValue } from '@app/account/notifications/interfaces/notifications-data.interfaces';
import { NotificationActionService } from '@app/account/notifications-and-alerts/notification-action.service';

type NotificationsState = {
  loading: boolean;
  data: ChannelNotification[];
  error: Error | unknown | null;
};

/**
 * Represents the optimistic adjustments to apply to each notification count.
 * Positive values increment, negative values decrement.
 */
interface INotificationsCountAdjustments {
  totalNotifications?: number;
  filteredNotifications?: number;
  unreadNotifications?: number;
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsOrchestratorService {
  // Dependencies
  private _notificationDataService = inject(NotificationsDataService);
  private _messageService = inject(MessageService);
  private _notificationActionService = inject(NotificationActionService);

  readonly PAGE_SIZE = 20;

  readonly INITIAL_STATE: NotificationsState = {
    loading: true,
    data: [],
    error: null,
  } as const;

  private _appliedFilters = signal<NotificationAlertFilterValue>(
    NotificationAlertFilterValue.UnreadAndRead,
  );

  /**
   * Triggers a full refresh of the current view. Used after bulk actions (e.g. mark all as read, archive all)
   * where we intentionally reset the list to page one rather than trying to maintain scroll position,
   * since the bulk operation invalidates the current pagination state.
   *
   * startWith(undefined) is required because combineLatest only emits once every source has emitted
   * at least one value. Without it, the initial fetch would never fire since nothing has triggered the subject yet.
   */
  private _refreshSubject = new Subject<void>();

  private _params$ = combineLatest({
    filterAttributes: toObservable(this._appliedFilters).pipe(
      map((filter: NotificationAlertFilterValue) =>
        this._getChannelNotificationFilterAttributes(filter),
      ),
    ),
    refresh: this._refreshSubject.asObservable().pipe(startWith(undefined)),
  });

  /** Notifications received from the real-time subscription, rendered in the headless toast component. */
  private _incomingNotifications = signal<ChannelNotification[]>([]);

  /** Notifications appended via infinite scroll pagination. */
  private _appendedNotifications = signal<ChannelNotification[]>([]);

  // Pagination
  private _endCursor = signal<string | null>(null);
  private _hasNextPage = signal<boolean>(false);
  private _loadingMore = signal<boolean>(false);

  /**
   * The cursor that STARTED the last page fetch (i.e. the endCursor of the previous page).
   * Always points to an item unaffected by mutations on the current page, so it remains
   * valid as a backfill anchor even when _endCursor becomes stale.
   */
  private _backfillCursor = signal<string | null>(null);

  // Optimistic updates
  private _optimisticChanges = signal<
    Map<string, Partial<ChannelNotification>>
  >(new Map());

  /** The IDs of the notifications that have been flagged for removal. */
  private _flaggedNotificationIds = signal<Set<string>>(new Set());

  // Counts
  /** The number of filtered notifications which correlates with the totalCount variable on the getNotifications query. */
  filteredNotificationsCount = signal<number>(0);
  unreadNotificationsCount = signal<number>(0);
  totalNotificationsCount = signal<number>(0);

  // Core state driven by params
  state = toSignal(
    this._params$.pipe(
      tap(() => this._resetLocalState()),
      switchMap(({ filterAttributes }) =>
        this._notificationDataService
          .getNotifications$(filterAttributes, this.PAGE_SIZE)
          .pipe(
            take(1),
            tap((result: PaginatedNotificationsResult) => {
              this._updatePaginationState(result.pageInfo);
              this.filteredNotificationsCount.set(result.totalCount);
            }),
            map(
              (result): NotificationsState => ({
                loading: false,
                data: result.nodes,
                error: null,
              }),
            ),
            startWith(this.INITIAL_STATE),
            catchError((err: Error) =>
              of<NotificationsState>({
                loading: false,
                data: [],
                error: err,
              }),
            ),
          ),
      ),
    ),
    { initialValue: this.INITIAL_STATE },
  );

  // Computed signals
  notifications = computed<ChannelNotification[]>(() => {
    const allNotifications: ChannelNotification[] = this._getAllNotifications();
    const uniqueNotifications: ChannelNotification[] =
      this._deduplicateById(allNotifications);
    const visibleNotifications: ChannelNotification[] =
      this._removeFlaggedNotifications(uniqueNotifications);
    return this._mergeOptimisticChanges(visibleNotifications);
  });

  loading = computed(() => this.state().loading);
  loadingMore = this._loadingMore.asReadonly();
  hasNextPage = this._hasNextPage.asReadonly();
  error = computed(() => this.state().error);

  constructor() {
    this._fetchNotificationCounts();

    effect(() => {
      const error = this.error();
      if (error) {
        console.error('error', error);
        captureException(error);
      }
    });
  }

  onFilterNotifications(filter: NotificationAlertFilterValue): void {
    this._appliedFilters.set(filter);
  }

  /** Adds an incoming notification from the real-time subscription to the list of notifications. */
  addIncomingNotification(notification: ChannelNotification): void {
    if (!notification?.id) return;

    // Do not show archived notifications in the notifications list
    const isArchivedView: boolean =
      this._appliedFilters() === NotificationAlertFilterValue.Archived;

    // Do not show alerts in the notifications list
    const isAlert: boolean =
      notification.notificationRule?.severity ===
      NotificationRuleSeverityEnum.Alert;

    if (isArchivedView || isAlert) return;

    // Prevent duplicates in case the getNotifications and real-time subscription happen simultaneously
    const isDuplicate: boolean = this._getAllNotifications().some(
      (n: ChannelNotification) => n.id === notification.id,
    );
    if (isDuplicate) return;

    this._incomingNotifications.update((prev: ChannelNotification[]) => [
      notification,
      ...prev,
    ]);

    this._adjustCountsOptimistically({
      totalNotifications: 1,
      filteredNotifications: 1,
      unreadNotifications: notification.readAt ? 0 : 1,
    });
  }

  /** Triggered when the user clicks on the Dismiss/Close/Got it and CTA buttons in the headless toast component. */
  onMarkIncomingNotificationAsRead(notification: ChannelNotification): void {
    this.onMarkNotificationAsRead(notification);
  }

  loadMore(): void {
    if (!this._hasNextPage() || this._loadingMore() || this.loading()) {
      return;
    }

    this._loadingMore.set(true);

    // Snapshot current cursor as backfill anchor before this fetch advances it.
    this._backfillCursor.set(this._endCursor());

    const filterAttributes: ChannelNotificationFilterAttributes =
      this._getChannelNotificationFilterAttributes(this._appliedFilters());

    this._notificationDataService
      .getNotifications$(filterAttributes, this.PAGE_SIZE, this._endCursor())
      .pipe(
        take(1),
        tap((result: PaginatedNotificationsResult) => {
          this._appendedNotifications.update((prev: ChannelNotification[]) => [
            ...prev,
            ...result.nodes,
          ]);
          this._updatePaginationState(result.pageInfo);
          this._loadingMore.set(false);
        }),
        catchError((err: Error) => {
          this._loadingMore.set(false);
          return this._handleError(err);
        }),
      )
      .subscribe();
  }

  onMarkNotificationAsRead(notification: ChannelNotification): void {
    if (!notification?.id) return;
    if (notification?.readAt) return;

    this._applyMarkAsReadOptimisticUpdates([notification.id]);
    this._markNotificationsAsRead([notification.id]);
  }

  onMarkNotificationAsUnread(notification: ChannelNotification): void {
    if (!notification?.id) return;

    this._applyOptimisticUpdates([notification.id], {
      readAt: null,
      archivedAt: null,
    });
    this._adjustCountsOptimistically({ unreadNotifications: 1 });

    // If the notification is already archived, remove it from the visible list of archived notifications
    // and decrement the filtered notifications count. As archived notificaitons that are marked as unread
    // must be 'unarchived' as well.
    if (notification.archivedAt) {
      this._flagNotificationsForRemoval([notification.id]);
      this._adjustCountsOptimistically({ filteredNotifications: -1 });
    }

    this._markNotificationsAsUnread([notification.id]);
  }

  onMarkAllAsRead(ids: string[]): void {
    if (!ids?.length) return;

    this._applyMarkAsReadOptimisticUpdates(ids);
    this._markNotificationsAsRead(ids, true);
  }

  onNotificationClick(notification: ChannelNotification): void {
    if (!notification?.notificationRule?.notificationSlug) return;

    // Mark the notification as read if it isn't
    if (!notification.readAt) {
      this.onMarkNotificationAsRead(notification);
    }

    this._notificationActionService.executeAction(
      notification.channelMetadata,
      notification.notificationRule.notificationSlug,
    );
  }

  onArchiveNotification(notification: ChannelNotification): void {
    if (!notification?.id) return;

    this._applyArchiveOptimisticUpdates([notification.id]);
    this._archiveNotifications([notification.id]);
  }

  onArchiveAllReadNotifications(ids: string[]): void {
    if (!ids?.length) return;

    this._applyArchiveOptimisticUpdates(ids);
    this._archiveNotifications(ids, true);
  }

  onArchiveAllNotifications(ids: string[]): void {
    if (!ids?.length) return;

    this._applyArchiveOptimisticUpdates(ids);
    this._archiveNotifications(ids, true);
  }

  /**
   * Marks notifications as read on the backend.
   * @param ids - The IDs of the notifications to mark as read.
   * @param refreshView - When true, refreshes the current view after the server responds.
   *   Used for bulk actions where the list and counts need to be re-synced with the server.
   *   Defaults to false for single-item actions where optimistic updates are sufficient.
   */
  private _markNotificationsAsRead(
    ids: string[],
    refreshView: boolean = false,
  ): void {
    this._notificationDataService
      .markNotificationsAsRead$(ids)
      .pipe(
        take(1),
        tap(() => {
          if (refreshView) {
            this._refreshCurrentView();
          } else {
            this._backfillNotifications();
          }
        }),
        catchError((err: Error) => this._handleError(err)),
      )
      .subscribe();
  }

  private _markNotificationsAsUnread(ids: string[]): void {
    this._notificationDataService
      .markNotificationsAsUnread$(ids)
      .pipe(
        take(1),
        tap(() => this._backfillNotifications()),
        catchError((err: Error) => this._handleError(err)),
      )
      .subscribe();
  }

  /**
   * Archives notifications on the backend.
   * @param ids - The IDs of the notifications to archive.
   * @param refreshView - When true, refreshes the current view after the server responds.
   *   Used for bulk actions where the list and counts need to be re-synced with the server.
   *   Defaults to false for single-item actions where optimistic updates are sufficient.
   */
  private _archiveNotifications(
    ids: string[],
    refreshView: boolean = false,
  ): void {
    if (!ids?.length) return;

    this._notificationDataService
      .archiveNotifications$(ids)
      .pipe(
        take(1),
        tap(() => {
          if (refreshView) {
            this._refreshCurrentView();
          } else {
            this._backfillNotifications();
          }
        }),
        catchError((err: Error) => this._handleError(err)),
      )
      .subscribe();
  }

  /**
   * Re-fetches from _backfillCursor to fill gaps after single-item removals
   * and recalibrate _endCursor for subsequent loadMore calls.
   */
  private _backfillNotifications(): void {
    if (this._loadingMore() || this.loading()) return;

    const visibleCount: number = this.notifications().length;
    const serverCount: number = this.filteredNotificationsCount();

    // All items are already loaded — nothing to backfill
    if (visibleCount >= serverCount) return;

    this._loadingMore.set(true);

    const filterAttributes: ChannelNotificationFilterAttributes =
      this._getChannelNotificationFilterAttributes(this._appliedFilters());

    this._notificationDataService
      .getNotifications$(
        filterAttributes,
        this.PAGE_SIZE,
        this._backfillCursor(),
      )
      .pipe(
        take(1),
        tap((result: PaginatedNotificationsResult) => {
          this._appendedNotifications.update((prev: ChannelNotification[]) => [
            ...prev,
            ...result.nodes,
          ]);
          this._updatePaginationState(result.pageInfo);
          this._loadingMore.set(false);
        }),
        catchError((err: Error) => {
          this._loadingMore.set(false);
          return this._handleError(err);
        }),
      )
      .subscribe();
  }

  /** Applies optimistic updates for marking as read while adjusting unread notifications count. */
  private _applyMarkAsReadOptimisticUpdates(ids: string[]): void {
    if (!ids?.length) return;

    const unreadIds: string[] = this._getUnreadIds(ids);
    const readAtTimestamp: string = new Date().toISOString();

    this._applyOptimisticUpdates(ids, { readAt: readAtTimestamp });
    this._adjustCountsOptimistically({
      unreadNotifications: -unreadIds.length,
    });

    // On the unread filter, read notifications no longer match the view
    if (this._appliedFilters() === NotificationAlertFilterValue.UnreadOnly) {
      this._flagNotificationsForRemoval(unreadIds);
      this._adjustCountsOptimistically({
        filteredNotifications: -unreadIds.length,
      });
    }
  }

  /** Applies optimistic updates for archiving and flags notifications for removal while adjusting counts. */
  private _applyArchiveOptimisticUpdates(ids: string[]): void {
    if (!ids?.length) return;

    const unreadNotificationIds: string[] = this._getUnreadIds(ids);
    const readAtTimestamp: string = new Date().toISOString();

    if (unreadNotificationIds.length) {
      this._applyOptimisticUpdates(unreadNotificationIds, {
        readAt: readAtTimestamp,
      });
    }

    this._flagNotificationsForRemoval(ids);

    this._adjustCountsOptimistically({
      filteredNotifications: -ids.length,
      unreadNotifications: -unreadNotificationIds.length,
    });
  }

  /** Applies optimistic changes to a list of notifications by ID. */
  private _applyOptimisticUpdates(
    ids: string[],
    changes: Partial<ChannelNotification>,
  ): void {
    this._optimisticChanges.update(
      (currentChanges: Map<string, Partial<ChannelNotification>>) => {
        const nextChanges: Map<string, Partial<ChannelNotification>> = new Map(
          currentChanges,
        );
        ids.forEach((id: string) =>
          nextChanges.set(id, { ...nextChanges.get(id), ...changes }),
        );
        return nextChanges;
      },
    );
  }

  /** Flags notifications for removal from the visible list before the server confirms the operation. */
  private _flagNotificationsForRemoval(ids: string[]): void {
    this._flaggedNotificationIds.update((currentIds: Set<string>) => {
      const nextIds: Set<string> = new Set(currentIds);
      ids.forEach((id: string) => nextIds.add(id));
      return nextIds;
    });
  }

  /**
   * Adjusts notification counts optimistically by the given adjustments.
   * Math.max(0, ...) is used as a safeguard to prevent counts from going negative.
   * This can happen when optimistic updates and server responses arrive out of order,
   * or when multiple rapid user actions cause overlapping adjustments.
   */
  private _adjustCountsOptimistically(
    adjustments: INotificationsCountAdjustments,
  ): void {
    if (adjustments.totalNotifications) {
      this.totalNotificationsCount.update((count: number) =>
        Math.max(0, count + adjustments.totalNotifications!),
      );
    }
    if (adjustments.filteredNotifications) {
      this.filteredNotificationsCount.update((count: number) =>
        Math.max(0, count + adjustments.filteredNotifications!),
      );
    }
    if (adjustments.unreadNotifications) {
      this.unreadNotificationsCount.update((count: number) =>
        Math.max(0, count + adjustments.unreadNotifications!),
      );
    }
  }

  /**
   * Refreshes the current view and notification counts.
   * Used after bulk actions to ensure the list and counts are in sync with the server.
   */
  private _refreshCurrentView(): void {
    this._refreshSubject.next();
    this._fetchNotificationCounts();
  }

  private _fetchNotificationCounts(): void {
    this._notificationDataService
      .getTotalChannelNotificationsCount$(NotificationRuleSeverityEnum.Info)
      .pipe(
        take(1),
        tap((count: number) => this.totalNotificationsCount.set(count)),
        catchError((err: Error) => this._handleError(err)),
      )
      .subscribe();

    this._notificationDataService
      .getUnreadChannelNotificationsCount$(NotificationRuleSeverityEnum.Info)
      .pipe(
        take(1),
        tap((count: number) => this.unreadNotificationsCount.set(count)),
        catchError((err: Error) => this._handleError(err)),
      )
      .subscribe();
  }

  /** Removes duplicate notifications, keeping the first occurrence of each ID. */
  private _deduplicateById(
    notifications: ChannelNotification[],
  ): ChannelNotification[] {
    const seenIds: Set<string> = new Set<string>();
    return notifications.filter((notification: ChannelNotification) => {
      if (seenIds.has(notification.id)) return false;
      seenIds.add(notification.id);
      return true;
    });
  }

  /** Filters out notifications that have been flagged for removal. */
  private _removeFlaggedNotifications(
    notifications: ChannelNotification[],
  ): ChannelNotification[] {
    const flaggedIds: Set<string> = this._flaggedNotificationIds();
    return notifications.filter(
      (notification: ChannelNotification) => !flaggedIds.has(notification.id),
    );
  }

  /** Returns a new list with any pending optimistic changes merged into the corresponding notifications. */
  private _mergeOptimisticChanges(
    notifications: ChannelNotification[],
  ): ChannelNotification[] {
    const pendingChanges: Map<
      string,
      Partial<ChannelNotification>
    > = this._optimisticChanges();
    return notifications.map((notification: ChannelNotification) => {
      const changes: Partial<ChannelNotification> | undefined =
        pendingChanges.get(notification.id);
      return changes ? { ...notification, ...changes } : notification;
    });
  }

  /** Returns the raw, unprocessed list of all notifications across all sources. */
  private _getAllNotifications(): ChannelNotification[] {
    return [
      ...this._incomingNotifications(),
      ...this.state().data,
      ...this._appendedNotifications(),
    ];
  }

  private _getUnreadIds(ids: string[]): string[] {
    const allNotifications: ChannelNotification[] = this._getAllNotifications();
    const pendingChanges: Map<
      string,
      Partial<ChannelNotification>
    > = this._optimisticChanges();

    return ids.filter((id: string) => {
      const notification: ChannelNotification | undefined =
        allNotifications.find((n: ChannelNotification) => n.id === id);
      if (!notification) return false;

      const changes: Partial<ChannelNotification> | undefined =
        pendingChanges.get(id);
      const effectiveReadAt: string | undefined =
        changes?.readAt !== undefined ? changes.readAt : notification.readAt;

      return !effectiveReadAt;
    });
  }

  private _resetLocalState(): void {
    this._clearAccumulatedNotifications();
    this._clearPendingOptimisticUpdates();
    this._resetPaginationState();
  }

  private _clearAccumulatedNotifications(): void {
    this._incomingNotifications.set([]);
    this._appendedNotifications.set([]);
  }

  private _clearPendingOptimisticUpdates(): void {
    this._optimisticChanges.set(new Map());
    this._flaggedNotificationIds.set(new Set());
  }

  private _resetPaginationState(): void {
    this._endCursor.set(null);
    this._hasNextPage.set(false);
    this._loadingMore.set(false);
    this._backfillCursor.set(null);
  }

  private _updatePaginationState(pageInfo: {
    endCursor: string | null;
    hasNextPage: boolean;
  }): void {
    this._endCursor.set(pageInfo.endCursor);
    this._hasNextPage.set(pageInfo.hasNextPage);
  }

  private _getChannelNotificationFilterAttributes(
    filter: NotificationAlertFilterValue | null,
  ): ChannelNotificationFilterAttributes {
    const baseChannelNotificationFilterAttributes: ChannelNotificationFilterAttributes =
      {
        archived: false,
        unreadAndRead: true,
        unread: false,
        severity: NotificationRuleSeverityEnum.Info,
      };

    switch (filter) {
      case NotificationAlertFilterValue.UnreadOnly:
        return {
          ...baseChannelNotificationFilterAttributes,
          unreadAndRead: false,
          unread: true,
        };
      case NotificationAlertFilterValue.Archived:
        return {
          ...baseChannelNotificationFilterAttributes,
          unreadAndRead: false,
          archived: true,
        };
      default:
        return baseChannelNotificationFilterAttributes;
    }
  }

  private _handleError(error: Error): Observable<never> {
    console.error('Error:', error);

    captureException(error);

    this._messageService.add({
      severity: 'error',
      summary: error.message,
    });
    return EMPTY;
  }
}
